
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  input,
  InputSignal,
  model as modelNg,
  ModelSignal,
  Output,
  Signal,
  signal,
  viewChild,
  WritableSignal
} from '@angular/core';
import { SideMenuTitleComponent } from "@shared/components/side-menu/components/side-menu-title/side-menu-title.component";
import { SideMenuContentComponent } from "@shared/components/side-menu/components/side-menu-content/side-menu-content.component";
import { SideMenuGroupComponent } from "@shared/components/side-menu/components/side-menu-group/side-menu-group.component";
import {
  SideMenuGroupTitleComponent
} from "@shared/components/side-menu/components/side-menu-group/components/side-menu-group-header/side-menu-group-title.component";
import {
  SideMenuGroupContentComponent
} from "@shared/components/side-menu/components/side-menu-group/components/side-menu-group-content/side-menu-group-content.component";
import { MatRadioChange, MatRadioModule } from '@angular/material/radio';
import { provideTranslationScope, TranslationPipeModule } from '@ogs-gmbh/ngx-translate';
import { SideMenuComponent } from "@shared/components/side-menu/side-menu.component";
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { LocalizedDatepickerDirective } from "@shared/directives/localized-datepicker/localized-datepicker.directive";
import { MatInputModule } from '@angular/material/input';
import {
  PROJECT_SPECIAL_TIME_TYPES,
  ProjectSpecialTime,
  ProjectSpecialTimeType,
  ProjectSpecialTimeWithoutId
} from '../../types/project-special-time.type';
import { TextFieldModule } from '@angular/cdk/text-field';
import { FormBuilder, FormGroupDirective, ReactiveFormsModule, Validators } from '@angular/forms';
import { FormStateService } from '@core/services/form-state.service';
import { DirtyCheckDirective } from '@shared/directives/dirty-check/dirty-check.directive';
import { FormOf } from '@shared/types/form-of.type';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { DATE_TYPES, DatePeriod, DateType, Time, TIME_TYPES, TimePeriod, TimeType } from '@shared/types/period.type';
import { filter } from 'rxjs';
import { DeleteButtonComponent } from "@shared/components/buttons/delete-button/delete-button.component";
import { InlineDeleteConfirmationComponent } from "@shared/components/inline-delete-confirmation/inline-delete-confirmation.component";
import { InlineConfirmationTriggerDirective } from "@shared/components/inline-confirmation/directives/inline-confirmation-trigger.directive";
import { isDate } from 'date-fns';
import { isString } from 'es-toolkit';
import { ProjectSpecialTimeUpdate } from '../../types/project-special-time-update.type';
import { TimeInputComponent } from "@shared/components/input/time-input/time-input.component";

interface SpecialTimesForm {
  startDate: Date | null;
  endDate: Date | null;
  startTime: string | null;
  endTime: string | null;
  note: string | null;
}

function parseFormToProjectSpecialTime (formValue: Partial<SpecialTimesForm>, timeType: TimeType, dateType: DateType, specialTimeType: ProjectSpecialTimeType): ProjectSpecialTimeWithoutId | undefined {
  let datePeriod: DatePeriod;
  let timePeriod: TimePeriod;

  if (!isDate(formValue.startDate))
    return;

  if (dateType === DATE_TYPES.single) {
    datePeriod = {
      type: DATE_TYPES.single,
      date: formValue.startDate
    };
  } else {
    if (!isDate(formValue.endDate)) return undefined;

    datePeriod = {
      type: DATE_TYPES.timespan,
      startDate: formValue.startDate,
      endDate: formValue.endDate
    };
  }

  if (timeType === TIME_TYPES.wholeDay) {
    timePeriod = {
      type: TIME_TYPES.wholeDay
    };
  } else {
    if (!isString(formValue.startTime) || !isString(formValue.endTime)) return undefined;

    const parsedStartTime: Time | undefined = formValue.startTime ? Time.formString(formValue.startTime) : undefined;
    const parsedEndTime: Time | undefined = formValue.endTime ? Time.formString(formValue.endTime) : undefined;

    if (!parsedStartTime || !parsedEndTime) return undefined;

    timePeriod = {
      type: TIME_TYPES.timespan,
      startTime: parsedStartTime,
      endTime: parsedEndTime
    };
  }

  return { type: specialTimeType, note: formValue.note ?? "", datePeriod, timePeriod };
}

const INITIAL_SPECIAL_TIME_TYPE: ProjectSpecialTimeType = PROJECT_SPECIAL_TIME_TYPES.closing;
const INITIAL_DATE_TYPE: DateType = DATE_TYPES.single;
const INITIAL_TIME_TYPE: TimeType = TIME_TYPES.timespan;

@Component({
  selector: 'app-project-special-time-menu',
  standalone: true,
  imports: [ SideMenuTitleComponent,
    SideMenuContentComponent,
    SideMenuGroupComponent,
    SideMenuGroupTitleComponent,
    SideMenuGroupContentComponent,
    MatRadioModule,
    TranslationPipeModule,
    SideMenuComponent,
    MatButtonModule,
    MatDatepickerModule,
    MatFormFieldModule,
    LocalizedDatepickerDirective,
    MatInputModule,
    TextFieldModule,
    ReactiveFormsModule,
    DirtyCheckDirective,
    DeleteButtonComponent,
    InlineDeleteConfirmationComponent,
    InlineConfirmationTriggerDirective,
    TimeInputComponent ],
  providers: [
    provideTranslationScope("feature/project-special-time-menu"),
    FormStateService
  ],
  templateUrl: './project-special-time-menu.component.html',
  styleUrl: './project-special-time-menu.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProjectSpecialTimeEditMenuComponent {
  protected readonly _formBuilder: FormBuilder;

  private readonly _formDirective: Signal<FormGroupDirective> = viewChild.required(FormGroupDirective);

  private readonly _dirtyStateService: FormStateService = inject(FormStateService);

  // eslint-disable-next-line @unicorn/no-useless-undefined
  public readonly editSpecialTime: InputSignal<ProjectSpecialTime | undefined> = input<ProjectSpecialTime | undefined>(undefined);

  private readonly _sideMenu: Signal<SideMenuComponent> = viewChild.required(SideMenuComponent);

  @Output() public readonly update: EventEmitter<ProjectSpecialTimeUpdate> = new EventEmitter<ProjectSpecialTimeUpdate>();

  @Output() public readonly add: EventEmitter<ProjectSpecialTimeWithoutId> = new EventEmitter<ProjectSpecialTimeWithoutId>();

  @Output() public readonly delete: EventEmitter<ProjectSpecialTime> = new EventEmitter<ProjectSpecialTime>();

  public isExpanded: ModelSignal<boolean> = modelNg(false);

  protected isDeleteConfirmationOpen: ModelSignal<boolean> = modelNg(false);

  protected selectedDateType: WritableSignal<DateType> = signal<DateType>(INITIAL_DATE_TYPE);

  protected selectedTimeType: WritableSignal<TimeType> = signal<TimeType>(INITIAL_TIME_TYPE);

  protected selectedSpecialTimeType: WritableSignal<ProjectSpecialTimeType> = signal<ProjectSpecialTimeType>(INITIAL_SPECIAL_TIME_TYPE);

  protected form!: FormOf<SpecialTimesForm>;

  // TODO: clean up constructor
  constructor () {
    this._formBuilder = new FormBuilder();
    this.buildForm();
    toObservable(this.editSpecialTime)
      .pipe(
        takeUntilDestroyed()
      )
      .subscribe((editSpecialTime: ProjectSpecialTime | undefined) => {
        if (!editSpecialTime) {
          this.reset();

          return;
        }

        this.selectedSpecialTimeType.set(editSpecialTime.type);
        this.selectedTimeType.set(editSpecialTime.timePeriod.type);
        this.selectedDateType.set(editSpecialTime.datePeriod.type);
        this.setFormValue(editSpecialTime);
      });
    toObservable(this.isExpanded)
      .pipe(
        filter(Boolean)
      )
      .subscribe(() => {
        const editSpecialTime: ProjectSpecialTime | undefined = this.editSpecialTime();

        if (editSpecialTime) {
          this.selectedSpecialTimeType.set(editSpecialTime.type);
          this.selectedTimeType.set(editSpecialTime.timePeriod.type);
          this.selectedDateType.set(editSpecialTime.datePeriod.type);
          this.setFormValue(editSpecialTime);
        } else this.reset();
      });
    toObservable(this.selectedDateType)
      .pipe(
        takeUntilDestroyed()
      )
      .subscribe((type: DateType) => {
        if (type === DATE_TYPES.single)
          // eslint-disable-next-line @tseslint/unbound-method
          this.form.controls.endDate.removeValidators(Validators.required);

        else {
          // eslint-disable-next-line @tseslint/unbound-method
          this.form.controls.endDate.addValidators(Validators.required);
        }

        this.form.controls.endDate.updateValueAndValidity();
      });
    toObservable(this.selectedTimeType)
      .pipe(
        takeUntilDestroyed()
      )
      .subscribe((type: TimeType) => {
        if (type === TIME_TYPES.wholeDay) {
          // eslint-disable-next-line @tseslint/unbound-method
          this.form.controls.startTime.removeValidators(Validators.required);
          // eslint-disable-next-line @tseslint/unbound-method
          this.form.controls.endTime.removeValidators(Validators.required);
        } else {
          // eslint-disable-next-line @tseslint/unbound-method
          this.form.controls.startTime.addValidators(Validators.required);
          // eslint-disable-next-line @tseslint/unbound-method
          this.form.controls.endTime.addValidators(Validators.required);
        }

        this.form.controls.startTime.updateValueAndValidity();
        this.form.controls.endTime.updateValueAndValidity();
      });
  }

  private reset (): void {
    this.selectedSpecialTimeType.set(INITIAL_SPECIAL_TIME_TYPE);
    this.selectedDateType.set(INITIAL_DATE_TYPE);
    this.selectedTimeType.set(INITIAL_TIME_TYPE);
    this.isDeleteConfirmationOpen.set(false);
    this._dirtyStateService.clear();
    this._formDirective().resetForm();
  }


  private setFormValue (specialTime: ProjectSpecialTime): void {
    const timePeriod: TimePeriod = specialTime.timePeriod;
    const startTime: string | null = timePeriod.type === TIME_TYPES.timespan ? timePeriod.startTime.toIsoTimeString() : null;
    const endTime: string | null = timePeriod.type === TIME_TYPES.timespan ? timePeriod.endTime.toIsoTimeString() : null;
    const datePeriod: DatePeriod = specialTime.datePeriod;
    const startDate: Date = datePeriod.type === DATE_TYPES.single ? datePeriod.date : datePeriod.startDate;
    const endDate: Date | null = datePeriod.type === DATE_TYPES.timespan ? datePeriod.endDate : null;

    this.form.setValue({ startDate, endDate, startTime, endTime, note: specialTime.note });
  }

  private buildForm (): void {
    this.form = this._formBuilder.group({
      // eslint-disable-next-line @tseslint/unbound-method
      note: this._formBuilder.control<string | null>("", { validators: [ Validators.required ], nonNullable: true }),
      // eslint-disable-next-line @tseslint/unbound-method
      startDate: this._formBuilder.control<Date | null>(null, { validators: [ Validators.required ] }),
      endDate: this._formBuilder.control<Date | null>(null),
      startTime: this._formBuilder.control<string | null>(null),
      endTime: this._formBuilder.control<string | null>(null)
    });
  }


  protected changeDateType (radioChange: MatRadioChange): void {
    const newDateType: DateType = radioChange.value as DateType;

    this.selectedDateType.set(newDateType);
    this.markDirty();
  }


  protected changeTimeType (radioChange: MatRadioChange): void {
    const newTimeType: TimeType = radioChange.value as TimeType;

    this.selectedTimeType.set(newTimeType);
    this.markDirty();
  }


  protected changeSpecialTimeType (radioChange: MatRadioChange): void {
    const newTimeType: ProjectSpecialTimeType = radioChange.value as ProjectSpecialTimeType;

    this.selectedSpecialTimeType.set(newTimeType);
    this.markDirty();
  }


  private markDirty (): void {
    this.form.markAsDirty();
    this._dirtyStateService.markDirty();
  }

  protected submit (): void {
    const parsedValue: ProjectSpecialTimeWithoutId | undefined = parseFormToProjectSpecialTime(
      this.form.value,
      this.selectedTimeType(),
      this.selectedDateType(),
      this.selectedSpecialTimeType()
    );

    if (!parsedValue) return;

    const editSpecialTime: ProjectSpecialTime | undefined = this.editSpecialTime();

    if (editSpecialTime) {
      this.update.emit({ oldSpecialTime: editSpecialTime,
        newSpecialTime: {
          ...parsedValue,
          id: editSpecialTime.id
        } });
    } else
      this.add.emit(parsedValue);
  }

  protected _delete (): void {
    const editSpecialTime: ProjectSpecialTime | undefined = this.editSpecialTime();

    if (editSpecialTime)
      this[ "delete" ].emit(editSpecialTime);
  }


  protected cancel (): void {
    this._sideMenu().closeMenu();
  }
}
